import { useEffect } from 'react';
import { Modal, Form, Skeleton, Alert } from 'antd';
import { applyCpfCnpjMask, applyMask, removeMask } from '@/utils/mask';
import { cepMask } from '@/utils/mask/commonMasks';
import { useDefaultFormErrorNotification } from '@/hooks';
import {
  CreateCustomerRequest,
  Customer,
  UpdateCustomerRequest,
} from '@/core/domain/people/Customers/Customers.types';
import {
  useCreateCustomer,
  useCustomerGeneral,
  useUpdateCustomer,
} from '@/core/domain/people/Customers/Customers.hooks';

import CustomerSimplifiedForm, {
  CustomerSimplifiedFormData,
} from '../CustomerSimplifiedForm';
import { CreateCustomerAddressRequest } from '@/core/domain/people/CustomerAddresses/CustomerAddresses.types';
import { AxiosError } from 'axios';
import moment from 'moment';
import { DateTimeFormat } from '@/utils/moment';
import { useCustomerAddresses } from '@/core/domain/people/CustomerAddresses/CustomerAddresses.hooks';
import { CreateContactRequest } from '@/core/domain/people/_shared/Contact';
import { ContactType } from '@/core/domain/_shared/ContactType';

interface CustomerSimplifiedRegisterModalProps {
  visible: boolean;
  handleVisible: (customer?: Customer) => void;
  customerId?: number;
  onFinishCallback?: (customer: Customer) => void;
  initialValues?: Partial<CustomerSimplifiedFormData>;
}

function CustomerSimplifiedRegisterModal({
  visible,
  customerId,
  handleVisible,
  onFinishCallback,
  initialValues,
}: CustomerSimplifiedRegisterModalProps) {
  const [form] = Form.useForm<CustomerSimplifiedFormData>();
  const { sendFormErrorNotification } = useDefaultFormErrorNotification();

  const isCustomerId = !!customerId && visible ? customerId : undefined;

  const { data: customer, isLoading } = useCustomerGeneral(isCustomerId);
  const customerAddressQuery = useCustomerAddresses(isCustomerId);

  const createMutation = useCreateCustomer();
  const updateMutation = useUpdateCustomer();

  const mainAddress = customerAddressQuery.data?.data?.find(
    (item) => item.main,
  );

  useEffect(() => {
    if (customer && visible) {
      let formObj: CustomerSimplifiedFormData = {
        name: customer.name,
        birthDate: customer.birthDate ? moment(customer.birthDate) : undefined,
        personType: customer.personType,
        michelinSegmentCode: customer?.michelinSegmentCode,
        genderType: customer.genderType,
        foreignDocument: customer?.foreignDocument,
        fantasyName: customer.fantasyName,
        municipalRegistration: customer.municipalRegistration,
        stateRegistration: customer.stateRegistration,
        retentionIRPJorCSLL: customer.retentionIRPJorCSLL,
        retentionISS: customer.retentionISS,
        enableLGPD:
          typeof customer.enableLGPD === 'boolean' ? customer.enableLGPD : null,
        recommendation: customer.recommendationId
          ? { id: customer.recommendationId }
          : undefined,
      };

      if (customer.cpfCnpj) {
        formObj = {
          ...formObj,
          cpfCnpj: applyCpfCnpjMask(customer.cpfCnpj),
        };
      }

      if (mainAddress) {
        formObj = {
          ...formObj,
          address: {
            ...mainAddress,
            zipCode: applyMask(removeMask(mainAddress.zipCode), cepMask),
          },
        };
      }

      form.setFieldsValue(formObj);
    } else if (initialValues && visible) {
      form.setFieldsValue(initialValues);
    }
  }, [customer, visible, initialValues]);

  const handleCreate = async () => {
    try {
      const {
        address,
        email: formEmail,
        phone: formPhone,
        ...values
      } = await form.validateFields();

      let obj: CreateCustomerRequest = {
        ...values,
        cpfCnpj: values.cpfCnpj || undefined,
        active: true,
        rentalCompany: false,
        birthDate: values.birthDate
          ? moment(values.birthDate).format(DateTimeFormat)
          : undefined,
        recommendation: values.recommendation?.id
          ? values.recommendation
          : undefined,
      };

      if (
        address &&
        address.cityId &&
        address.number &&
        address.street &&
        address.zipCode
      ) {
        const customerAddress: CreateCustomerAddressRequest = {
          complement: address.complement,
          district: address.district,
          cityId: address.cityId,
          city: address.city,
          state: address.state,
          number: address.number,
          street: address.street,
          zipCode: address.zipCode,
          delivery: true,
          main: true,
        };

        obj = { ...obj, addresses: [customerAddress] };
      }

      const contacts: CreateContactRequest[] = [];

      if (formEmail) {
        contacts.push({
          active: true,
          contactType: ContactType.Email,
          description: 'E-Mail',
          value: formEmail,
          main: true,
        });
      }

      if (formPhone) {
        contacts.push({
          active: true,
          contactType: ContactType.Mobile,
          description: 'Celular',
          value: removeMask(formPhone),
          main: true,
        });
      }

      if (contacts.length) {
        obj = {
          ...obj,
          contacts: [{ detailList: contacts, name: obj.name, main: true }],
        };
      }

      const result = await createMutation.mutateAsync(obj);

      if (onFinishCallback) {
        onFinishCallback(result);
      }

      handleCancel();
    } catch (err) {
      const error = err as AxiosError;

      if (!error.isAxiosError) {
        sendFormErrorNotification(err);
      }
    }
  };

  const handleEdit = async () => {
    try {
      if (!customer) return;

      const values = await form.validateFields();

      const obj: UpdateCustomerRequest = {
        ...customer,
        ...values,
        recommendation: values.recommendation?.id
          ? { id: values.recommendation?.id }
          : undefined,
        cpfCnpj: values.cpfCnpj ? removeMask(values.cpfCnpj) : undefined,
        active: true,
        birthDate: values.birthDate
          ? moment(values.birthDate).format(DateTimeFormat)
          : undefined,
      };

      const result = await updateMutation.mutateAsync({ id: customer.id, obj });

      if (onFinishCallback) {
        onFinishCallback(result);
      }

      handleCancel();
    } catch (err) {
      const error = err as AxiosError;

      if (!error.isAxiosError) {
        sendFormErrorNotification(err);
      }
    }
  };

  const handleOk = async () => {
    if (!customer) {
      await handleCreate();
    } else {
      await handleEdit();
    }
  };

  const handleCancel = () => {
    handleVisible();
    form.resetFields();
  };

  return (
    <Modal
      open={visible}
      onCancel={handleCancel}
      onOk={handleOk}
      title={
        !customer ? 'Cadastro rápido do cliente' : 'Edição rápida do cliente'
      }
      width={991}
      confirmLoading={createMutation.isLoading || updateMutation.isLoading}
      destroyOnClose
      okText={!customer ? 'Cadastrar' : 'Salvar'}
    >
      <Skeleton
        active
        loading={isLoading || customerAddressQuery.isLoading}
        paragraph={{ rows: 15 }}
      >
        <CustomerSimplifiedForm
          form={form}
          address={mainAddress}
          isEdit={!!customer}
          customerId={customer?.id}
          customer={customer}
          mainAddress={
            !!customerAddressQuery.data?.data.length ? !!mainAddress : true
          }
          recommendationId={customer?.recommendationId}
        />

        {customerId && (
          <Alert
            type="warning"
            showIcon
            message="Atenção! Ao editar o cliente, todas as informações do mesmo vinculado a outros orçamentos serão alteradas."
            style={{ marginTop: 12, borderRadius: 4 }}
          />
        )}
      </Skeleton>
    </Modal>
  );
}

export default CustomerSimplifiedRegisterModal;
